import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import api from '../api/client';
import { Expense, ExpenseCategory, Vehicle } from '../types';

const CATEGORIES: Array<{ value: ExpenseCategory; label: string; icon: string; color: string }> = [
  { value: 'combustivel', label: 'Combustível', icon: '⛽', color: '#0f766e' },
  { value: 'manutencao', label: 'Manutenção', icon: '🔧', color: '#2563eb' },
  { value: 'pedagio', label: 'Pedágio', icon: '🛣️', color: '#b45309' },
  { value: 'estacionamento', label: 'Estacionamento', icon: '🅿️', color: '#7c3aed' },
  { value: 'outros', label: 'Outros gastos', icon: '⋯', color: '#64748b' }
];

const today = () => new Date().toISOString().slice(0, 10);
const currentMonth = () => new Date().toISOString().slice(0, 7);
const currency = (value: number) => value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dateLabel = (value: string) => {
  const [year, month, day] = value.slice(0, 10).split('-');
  return year && month && day ? `${day}/${month}/${year}` : '—';
};
const categoryInfo = (category: ExpenseCategory) => CATEGORIES.find((item) => item.value === category) || CATEGORIES[4];

// Mascaras de entrada no padrao brasileiro
const formatIntegerBR = (num: number) => Math.round(num).toLocaleString('pt-BR');
const formatDecimal2BR = (num: number) => num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const maskDecimal2 = (input: string) => {
  const digits = input.replace(/\D/g, '');
  if (digits === '') return '';
  const num = parseInt(digits, 10);
  const cents = num % 100;
  const integer = Math.floor(num / 100);
  return integer.toLocaleString('pt-BR') + ',' + String(cents).padStart(2, '0');
};

const maskInteger = (input: string) => {
  const digits = input.replace(/\D/g, '');
  return digits === '' ? '' : parseInt(digits, 10).toLocaleString('pt-BR');
};

const unmaskNumber = (str: string): number | null => {
  if (!str || !str.trim()) return null;
  const normalized = str.replace(/\./g, '').replace(',', '.');
  const num = Number(normalized);
  return Number.isFinite(num) ? num : null;
};

export default function Expenses() {
  const { showToast } = useToast();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Expense | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<'all' | ExpenseCategory>('all');
  const [vehicleFilter, setVehicleFilter] = useState('all');
  const [month, setMonth] = useState(currentMonth());
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<ExpenseCategory>('combustivel');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(today());
  const [vehicleId, setVehicleId] = useState('');
  const [liters, setLiters] = useState('');
  const [odometerKm, setOdometerKm] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = { month };
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (vehicleFilter !== 'all') params.vehicleId = vehicleFilter;
      if (search.trim()) params.search = search.trim();
      const [expenseResponse, vehicleResponse] = await Promise.all([
        api.get<Expense[]>('/expenses', { params }),
        api.get<Vehicle[]>('/vehicles')
      ]);
      setExpenses(expenseResponse.data);
      setVehicles(vehicleResponse.data);
    } catch {
      showToast('Não foi possível carregar os lançamentos.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // A busca é aplicada somente ao pressionar Enter; os demais filtros recarregam automaticamente.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [month, categoryFilter, vehicleFilter]);

  const totals = useMemo(() => {
    const fuel = expenses.filter((item) => item.category === 'combustivel').reduce((sum, item) => sum + item.amount, 0);
    const total = expenses.reduce((sum, item) => sum + item.amount, 0);
    const litersTotal = expenses.filter((item) => item.category === 'combustivel').reduce((sum, item) => sum + (item.liters || 0), 0);
    return { total, fuel, other: total - fuel, litersTotal };
  }, [expenses]);

  const resetForm = () => {
    setEditing(null);
    setCategory('combustivel');
    setDescription('');
    setAmount('');
    setDate(today());
    setVehicleId('');
    setLiters('');
    setOdometerKm('');
  };

  const openCreate = () => { resetForm(); setModalOpen(true); };
  const openEdit = (expense: Expense) => {
    setEditing(expense);
    setCategory(expense.category);
    setDescription(expense.description);
    setAmount(formatDecimal2BR(expense.amount));
    setDate(expense.date.slice(0, 10));
    setVehicleId(expense.vehicleId || '');
    setLiters(expense.liters ? formatDecimal2BR(expense.liters) : '');
    setOdometerKm(expense.odometerKm ? formatIntegerBR(expense.odometerKm) : '');
    setModalOpen(true);
  };

  const handleSave = async () => {
    const parsedAmount = unmaskNumber(amount);
    if (!description.trim() || parsedAmount === null || parsedAmount <= 0) {
      showToast('Informe descrição e valor maior que zero.', 'error');
      return;
    }
    setSaving(true);
    const payload = { category, description: description.trim(), amount: parsedAmount, date, vehicleId: vehicleId || null, liters: unmaskNumber(liters), odometerKm: unmaskNumber(odometerKm) };
    try {
      if (editing) await api.patch(`/expenses/${editing.id}`, payload);
      else await api.post('/expenses', payload);
      showToast(editing ? 'Lançamento atualizado.' : 'Lançamento registrado.', 'success');
      setModalOpen(false);
      await load();
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Não foi possível salvar o lançamento.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (expense: Expense) => {
    if (!window.confirm(`Excluir o lançamento “${expense.description}”?`)) return;
    try {
      await api.delete(`/expenses/${expense.id}`);
      showToast('Lançamento excluído.', 'info');
      await load();
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Não foi possível excluir o lançamento.', 'error');
    }
  };

  return (
    <Layout>
      <div className="page-head">
        <div>
          <h1 className="page-title">Lançamentos operacionais</h1>
          <p className="page-lede">Registre abastecimentos, manutenção, pedágios e outros gastos da frota.</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>+ Novo lançamento</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 14, marginBottom: 20 }}>
        {[
          ['Total no período', currency(totals.total), '#082129'],
          ['Combustível', currency(totals.fuel), '#0f766e'],
          ['Outros gastos', currency(totals.other), '#b45309'],
          ['Litros abastecidos', `${totals.litersTotal.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} L`, '#2563eb']
        ].map(([label, value, color]) => (
          <div className="card" key={label} style={{ padding: '16px 18px' }}>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 7 }}>{label}</div>
            <div style={{ fontSize: 22, fontWeight: 750, color }}>{value}</div>
          </div>
        ))}
      </div>

      <div className="card" style={{ padding: 16, marginBottom: 18 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <div className="field"><label>Período</label><input className="input" type="month" value={month} onChange={(event) => setMonth(event.target.value)} /></div>
          <div className="field"><label>Categoria</label><select className="input" value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value as 'all' | ExpenseCategory)}><option value="all">Todas as categorias</option>{CATEGORIES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></div>
          <div className="field"><label>Veículo</label><select className="input" value={vehicleFilter} onChange={(event) => setVehicleFilter(event.target.value)}><option value="all">Todos os veículos</option>{vehicles.map((vehicle) => <option key={vehicle.id} value={vehicle.id}>{vehicle.name} · {vehicle.plate}</option>)}</select></div>
          <div className="field"><label>Buscar descrição</label><input className="input" value={search} onChange={(event) => setSearch(event.target.value)} onKeyDown={(event) => event.key === 'Enter' && load()} placeholder="Ex.: Posto, oficina..." /></div>
        </div>
      </div>

      <div className="card" style={{ overflowX: 'auto' }}>
        {loading ? <div className="empty"><p>Carregando lançamentos...</p></div> : expenses.length === 0 ? <div className="empty"><h4>Nenhum lançamento neste período</h4><p>Registre o primeiro gasto da frota para acompanhar a operação.</p></div> : (
          <table className="data expenses-table" style={{ width: '100%', minWidth: 920, tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '10%' }} /><col style={{ width: '14%' }} /><col style={{ width: '20%' }} />
              <col style={{ width: '15%' }} /><col style={{ width: '15%' }} /><col style={{ width: '11%' }} /><col style={{ width: '15%' }} />
            </colgroup>
            <thead><tr><th>Data</th><th>Categoria</th><th>Descrição</th><th>Veículo</th><th>Detalhes</th><th style={{ textAlign: 'right' }}>Valor</th><th style={{ textAlign: 'right' }}>Ações</th></tr></thead>
            <tbody>{expenses.map((expense) => {
              const info = categoryInfo(expense.category);
              return <tr key={expense.id}>
                <td className="mono">{dateLabel(expense.date)}</td>
                <td><span className="badge" style={{ background: `${info.color}18`, color: info.color }}>{info.icon} {info.label}</span></td>
                <td style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{expense.description}</td>
                <td>{expense.vehicle ? <><b>{expense.vehicle.name}</b><br /><span style={{ fontSize: 11, color: 'var(--text-3)' }}>{expense.vehicle.plate}</span></> : '—'}</td>
                <td style={{ fontSize: 12, color: 'var(--text-3)' }}>{expense.liters ? `${expense.liters.toLocaleString('pt-BR')} L` : ''}{expense.liters && expense.odometerKm ? ' · ' : ''}{expense.odometerKm ? `${expense.odometerKm.toLocaleString('pt-BR')} km` : '—'}</td>
                <td style={{ textAlign: 'right', fontWeight: 750 }}>{currency(expense.amount)}</td>
                <td><div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, flexWrap: 'wrap' }}><button className="btn btn-secondary btn-sm" onClick={() => openEdit(expense)}>Editar</button><button className="btn btn-danger btn-sm" onClick={() => remove(expense)}>Excluir</button></div></td>
              </tr>;
            })}</tbody>
          </table>
        )}
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Editar lançamento' : 'Novo lançamento'} maxWidth="620px" footer={<><button className="btn btn-secondary" onClick={() => setModalOpen(false)}>Cancelar</button><button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Salvando...' : 'Salvar lançamento'}</button></>}>
        <div className="field-row"><div className="field"><label>Categoria</label><select className="input" value={category} onChange={(event) => setCategory(event.target.value as ExpenseCategory)}>{CATEGORIES.map((item) => <option key={item.value} value={item.value}>{item.icon} {item.label}</option>)}</select></div><div className="field"><label>Data</label><input type="date" className="input" value={date} onChange={(event) => setDate(event.target.value)} /></div></div>
        <div className="field-row" style={{ marginTop: 12 }}><div className="field"><label>Descrição</label><input autoFocus className="input" value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Ex.: Abastecimento Posto Central" /></div><div className="field"><label>Valor total (R$)</label><input inputMode="numeric" className="input mono" value={amount} onChange={(event) => setAmount(maskDecimal2(event.target.value))} placeholder="0,00" /></div></div>
        <div className="field" style={{ marginTop: 12 }}><label>Veículo <span style={{ fontWeight: 400, color: 'var(--text-3)' }}>(opcional)</span></label><select className="input" value={vehicleId} onChange={(event) => setVehicleId(event.target.value)}><option value="">Gasto geral, sem veículo</option>{vehicles.map((vehicle) => <option key={vehicle.id} value={vehicle.id}>{vehicle.name} · {vehicle.plate}</option>)}</select></div>
        {category === 'combustivel' && <div className="field-row" style={{ marginTop: 12 }}><div className="field"><label>Litros abastecidos <span style={{ fontWeight: 400, color: 'var(--text-3)' }}>(opcional)</span></label><input inputMode="numeric" className="input mono" value={liters} onChange={(event) => setLiters(maskDecimal2(event.target.value))} placeholder="Ex.: 42,50" /></div><div className="field"><label>Hodômetro (km) <span style={{ fontWeight: 400, color: 'var(--text-3)' }}>(opcional)</span></label><input inputMode="numeric" className="input mono" value={odometerKm} onChange={(event) => setOdometerKm(maskInteger(event.target.value))} placeholder="Ex.: 125.400" /></div></div>}
      </Modal>
    </Layout>
  );
}
